<template>
  <Progressbar v-if="loading && !meta.current_page" />
  <NetworkError v-else-if="errors" @tryAgain="getData" />
  <div class="flex flex-row gap-6 w-full h-full" v-else>
    <div class="flex flex-1 flex-col overflow-hidden">
      <AnimatedNotice :messages="noticeMessages":interval="6000" />
      <div class="flex flex-nowrap gap-2 md:gap-4 items-center">
        <input type="text" v-model="search" :placeholder="__('Search by product name or code')"
          class="input w-full bg-blue-custom-1 h-10 md:h-12 text-gray-custom-1 placeholder-gray-custom-1" />
        <label class="label cursor-pointer justify-start py-0 lg:hidden" v-if="meta.show_out_stock">
          <input type="checkbox" :checked="$route.query.out_stock == 1"
            @change="setQuery('out_stock', Number($event.target.checked))" class="checkbox checkbox-xs" />
          <span class="label-text text-xs font-bold mr-2">{{ __('Out Stock') }}</span>
        </label>
        <div class="dropdown dropdown-end lg:hidden">
          <label tabindex="0" class="btn btn-sm btn-square btn-error px-2" @click="getCategories('brand', false)">
            <svg style="color: white" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-5 h-5 md:w-7 md:h-7"
              viewBox="0 0 16 16">
              <path
                d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"
                fill="white"></path>
              <path
                d="M4.603 12.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.701 19.701 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.187-.012.395-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.065.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.716 5.716 0 0 1-.911-.95 11.642 11.642 0 0 0-1.997.406 11.311 11.311 0 0 1-1.021 1.51c-.29.35-.608.655-.926.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.27.27 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.647 12.647 0 0 1 1.01-.193 11.666 11.666 0 0 1-.51-.858 20.741 20.741 0 0 1-.5 1.05zm2.446.45c.15.162.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.881 3.881 0 0 0-.612-.053zM8.078 5.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"
                fill="white"></path>
            </svg>
          </label>
          <div tabindex="0" class="mt-3 card card-compact dropdown-content w-72 bg-base-100 shadow">
            <div class="card-body">
              <select class="select w-full max-w-xs" v-model="exportData.brand">
                <option disabled value="">{{ __('Select Brand...') }}</option>
                <option v-for="brand in brands" :value="brand.id">{{ brand.title }}</option>
              </select>
              <div class="form-control mt-3">
                <label class="label cursor-pointer justify-start">
                  <input type="checkbox" v-model="exportData.in_stock" class="checkbox" />
                  <span class="label-text mr-3">{{ __('Only items in stock') }}</span>
                </label>
              </div>
              <div class="card-actions">
                <button class="btn btn-primary btn-block" @click="exportProduct">{{ __('Export') }}</button>
              </div>
            </div>
          </div>
        </div>
        <div class="hidden lg:flex w-44 sm:w-auto xl:flex-none bg-white rounded-lg">
          <div class="card-body p-0 sm:p-2 flex flex-row">
            <label class="label cursor-pointer justify-start py-0" v-if="meta.show_out_stock">
              <input type="checkbox" :checked="$route.query.out_stock == 1"
                @change="setQuery('out_stock', Number($event.target.checked))" class="checkbox" />
              <span class="label-text font-bold mr-3">{{ __('Out Stock') }}</span>
            </label>
            <label class="hidden lg:flex btn btn-sm text-xs sm:text-sm btn-outline !bg-white !text-gray-custom-1"
              @click="getCategories()" :class="{ '!btn-error': $route.query.category?.length }">
              <ListBulletIcon class="w-5 h-5 ml-1 xl:hidden 2xl:block" />
              <span class="lg:hidden xl:inline">{{ __('Category') }}</span>
            </label>
            <label class="hidden lg:flex btn btn-sm text-xs sm:text-sm btn-outline !bg-white !text-gray-custom-1"
              @click="getCategories('brand')" :class="{ '!btn-error': $route.query.brand?.length }">
              <AdjustmentsHorizontalIcon class="w-5 h-5 ml-1 xl:hidden 2xl:block" />
              <span class="lg:hidden xl:inline">{{ __('Brands') }}</span>
            </label>
            <label class="hidden lg:flex btn btn-sm btn-outline text-xs sm:text-sm !text-gray-custom-1 !bg-white"
              @click="getCategories('car')" :class="{ '!btn-error': $route.query.car?.length }">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5 ml-1 xl:hidden 2xl:block" fill="currentColor"
                viewBox="0 0 16 16">
                <path
                  d="M4 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm10 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6 8a1 1 0 0 0 0 2h4a1 1 0 1 0 0-2H6ZM4.862 4.276 3.906 6.19a.51.51 0 0 0 .497.731c.91-.073 2.35-.17 3.597-.17 1.247 0 2.688.097 3.597.17a.51.51 0 0 0 .497-.731l-.956-1.913A.5.5 0 0 0 10.691 4H5.309a.5.5 0 0 0-.447.276Z" />
                <path
                  d="M2.52 3.515A2.5 2.5 0 0 1 4.82 2h6.362c1 0 1.904.596 2.298 1.515l.792 1.848c.075.175.21.319.38.404.5.25.855.715.965 1.262l.335 1.679c.033.161.049.325.049.49v.413c0 .814-.39 1.543-1 1.997V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.338c-1.292.048-2.745.088-4 .088s-2.708-.04-4-.088V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.892c-.61-.454-1-1.183-1-1.997v-.413a2.5 2.5 0 0 1 .049-.49l.335-1.68c.11-.546.465-1.012.964-1.261a.807.807 0 0 0 .381-.404l.792-1.848ZM4.82 3a1.5 1.5 0 0 0-1.379.91l-.792 1.847a1.8 1.8 0 0 1-.853.904.807.807 0 0 0-.43.564L1.03 8.904a1.5 1.5 0 0 0-.03.294v.413c0 .796.62 1.448 1.408 1.484 1.555.07 3.786.155 5.592.155 1.806 0 4.037-.084 5.592-.155A1.479 1.479 0 0 0 15 9.611v-.413c0-.099-.01-.197-.03-.294l-.335-1.68a.807.807 0 0 0-.43-.563 1.807 1.807 0 0 1-.853-.904l-.792-1.848A1.5 1.5 0 0 0 11.18 3H4.82Z" />
              </svg>
              <span class="lg:hidden xl:inline">{{ __('Vehicles') }}</span>
            </label>
            <div class="dropdown">
              <label tabindex="0"
                class="hidden lg:flex btn btn-sm btn-outline text-xs sm:text-sm !text-gray-custom-1 !bg-white"
                :class="{ 'btn-error': $route.query.sort }">
                <FunnelIcon class="w-5 h-5 ml-1 xl:hidden 2xl:block" />
                <span class="lg:hidden xl:inline">
                  {{ __($route.query.sort ? $route.query.sort.replace('_', ' ') : 'the newest') }}
                </span>
              </label>
              <ul tabindex="0" class="mt-3 p-2 shadow menu menu-compact dropdown-content bg-base-100 rounded-box">
                <li><a class="text-black" @click.prevent="setQuery('sort', '')"
                    :class="{ 'bg-red-custom-1': !$route.query.sort }">
                    {{ __('the newest') }}
                  </a></li>
                <li><a class="text-black" @click="setQuery('sort', 'best_selling')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'best_selling' }">{{ __('Bestselling') }}</a></li>
                <li><a class="text-black" @click="setQuery('sort', 'min_price')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'min_price' }">{{ __('cheapest') }}</a></li>
                <li><a class="text-black" @click="setQuery('sort', 'max_price')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'max_price' }">{{ __('The most oriented') }}</a>
                </li>
              </ul>
            </div>
            <button class="hidden lg:flex btn btn-ghost btn-sm btn-square !text-gray-custom-1" @click="changeLayout">
              <Squares2X2Icon class="w-6 h-6 ml-1" v-if="grid" />
              <Bars3BottomLeftIcon class="w-6 h-6 ml-1" v-else />
            </button>
          </div>
        </div>
      </div>
      <div class="flex mt-3 lg:mt-4">
        <button @click="getCategories()"
          class="lg:hidden btn btn-sm btn-square btn-outline ml-2 !bg-white !text-gray-custom-1"
          :class="{ '!btn-error': $route.query.category?.length }">
          <ListBulletIcon class="w-5 h-5" />
        </button>
        <div class="alert py-0 px-2 md:px-4 h-8 lg:h-12 flex flex-row" :class="$route.meta.titleClass">
          <span class="my-auto lg:mx-auto truncate"> {{ $route.meta.title }}</span>
          <div class="flex flex-row !mt-0">
            <div class="dropdown">
              <label tabindex="0" class="lg:hidden btn btn-ghost btn-sm btn-square"
                :class="{ '!text-error': $route.query.sort }">
                <FunnelIcon class="w-5 h-5" />
              </label>
              <ul tabindex="0" class="mt-3 p-2 shadow menu menu-compact dropdown-content bg-base-100 rounded-box">
                <li><a class="text-black" @click.prevent="setQuery('sort', '')"
                    :class="{ 'bg-red-custom-1': !$route.query.sort }">
                    {{ __('the newest') }}
                  </a></li>
                <li><a class="text-black" @click="setQuery('sort', 'best_selling')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'best_selling' }">{{ __('Bestselling') }}</a></li>
                <li><a class="text-black" @click="setQuery('sort', 'min_price')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'min_price' }">{{ __('cheapest') }}</a></li>
                <li><a class="text-black" @click="setQuery('sort', 'max_price')"
                    :class="{ 'bg-red-custom-1': $route.query.sort == 'max_price' }">{{ __('The most oriented') }}</a>
                </li>
              </ul>
            </div>
            <button class="lg:hidden btn btn-sm btn-ghost btn-square text-lg " @click="getCategories('brand')"
              :class="{ '!text-error': $route.query.brand?.length }">
              B
            </button>
            <button class="lg:hidden btn btn-sm btn-ghost btn-square text-xs sm:text-sm " @click="getCategories('car')"
              :class="{ '!text-error': $route.query.car?.length }">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5" fill="currentColor" viewBox="0 0 16 16">
                <path
                  d="M4 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm10 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6 8a1 1 0 0 0 0 2h4a1 1 0 1 0 0-2H6ZM4.862 4.276 3.906 6.19a.51.51 0 0 0 .497.731c.91-.073 2.35-.17 3.597-.17 1.247 0 2.688.097 3.597.17a.51.51 0 0 0 .497-.731l-.956-1.913A.5.5 0 0 0 10.691 4H5.309a.5.5 0 0 0-.447.276Z" />
                <path
                  d="M2.52 3.515A2.5 2.5 0 0 1 4.82 2h6.362c1 0 1.904.596 2.298 1.515l.792 1.848c.075.175.21.319.38.404.5.25.855.715.965 1.262l.335 1.679c.033.161.049.325.049.49v.413c0 .814-.39 1.543-1 1.997V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.338c-1.292.048-2.745.088-4 .088s-2.708-.04-4-.088V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.892c-.61-.454-1-1.183-1-1.997v-.413a2.5 2.5 0 0 1 .049-.49l.335-1.68c.11-.546.465-1.012.964-1.261a.807.807 0 0 0 .381-.404l.792-1.848ZM4.82 3a1.5 1.5 0 0 0-1.379.91l-.792 1.847a1.8 1.8 0 0 1-.853.904.807.807 0 0 0-.43.564L1.03 8.904a1.5 1.5 0 0 0-.03.294v.413c0 .796.62 1.448 1.408 1.484 1.555.07 3.786.155 5.592.155 1.806 0 4.037-.084 5.592-.155A1.479 1.479 0 0 0 15 9.611v-.413c0-.099-.01-.197-.03-.294l-.335-1.68a.807.807 0 0 0-.43-.563 1.807 1.807 0 0 1-.853-.904l-.792-1.848A1.5 1.5 0 0 0 11.18 3H4.82Z" />
              </svg>
            </button>
            <button class="lg:hidden btn btn-ghost btn-sm btn-square " @click="changeLayout">
              <Squares2X2Icon class="w-5 h-5" v-if="grid" />
              <Bars3BottomLeftIcon class="w-5 h-5" v-else />
            </button>
          </div>
        </div>
        <div class="dropdown dropdown-end hidden lg:inline-block">
          <label tabindex="0" class="btn btn-square btn-error mr-3 px-2" @click="getCategories('brand', false)">
            <svg style="color: white" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4 lg:w-7 lg:h-7"
              viewBox="0 0 16 16">
              <path
                d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"
                fill="white"></path>
              <path
                d="M4.603 12.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.701 19.701 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.187-.012.395-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.065.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.716 5.716 0 0 1-.911-.95 11.642 11.642 0 0 0-1.997.406 11.311 11.311 0 0 1-1.021 1.51c-.29.35-.608.655-.926.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.27.27 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.647 12.647 0 0 1 1.01-.193 11.666 11.666 0 0 1-.51-.858 20.741 20.741 0 0 1-.5 1.05zm2.446.45c.15.162.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.881 3.881 0 0 0-.612-.053zM8.078 5.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"
                fill="white"></path>
            </svg>
          </label>
          <div tabindex="0" class="mt-3 card card-compact dropdown-content w-72 bg-base-100 shadow">
            <div class="card-body">
              <select class="select w-full max-w-xs" v-model="exportData.brand">
                <option disabled value="">{{ __('Select Brand...') }}</option>
                <option v-for="brand in brands" :value="brand.id">{{ brand.title }}</option>
              </select>
              <div class="form-control mt-3">
                <label class="label cursor-pointer justify-start">
                  <input type="checkbox" v-model="exportData.in_stock" class="checkbox" />
                  <span class="label-text mr-3">{{ __('Only items in stock') }}</span>
                </label>
              </div>
              <div class="card-actions">
                <button class="btn btn-primary btn-block" @click="exportProduct">{{ __('Export') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="card mt-3 lg:mt-4 bg-white product-item scrollbar-thin scrollbar-thumb-base-300 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-base-200 rounded-none"
        :class="[$route.meta.type, loading ? 'md:pointer-events-none !overflow-x-hidden' : '']" v-if="products.length">
        <div class="card-body" :class="[grid ? 'p-2 md:p-4' : 'p-0']">
          <ProductItem :products="products" :loading="loading" :search="$route.query.name || $route.query.code" :meta="meta"
            :grid="grid" @getProduct="showProduct" @changeQuantity="changeQuantity" />
          <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 2xl:gap-4" v-if="grid">
            <ProductGridItem v-for="(product, index) in products" :product="product"
              :search="$route.query.name || $route.query.code" :index="index" :loading="loading" :grid="grid"
              @getProduct="showProduct" @changeQuantity="changeQuantity" />
          </div>
        </div>
        <div class="h-full top-0 left-0 right-0 bottom-0 z-10 absolute hidden md:flex justify-center items-center"
          v-if="loading">
          <button class="btn btn-ghost loading btn-lg  !text-red-600 bg-gray-500/25"><span class="text-black text-base">{{
            __('please wait...') }}</span></button>
        </div>
        <LoadMore v-if="meta.current_page < meta.last_page" :errors="errors"
          @tryAgain="getProducts(meta.current_page + 1, true)" />
      </div>
      <NotFound v-else />
    </div>
    <LeftSidebar :large="true" />
  </div>
  <ProductModal :product="product" />
  <ReserveModal :reserve="reserve" @change="changeQuantity" />
  <ProductQuantityModal />
  <Teleport to="#app">
    <input type="checkbox" id="category-modal" class="modal-toggle" />
    <div class="modal" for="category-modal">
      <div class="modal-box max-w-5xl" for="">
        <input type="text" :placeholder="__('Search for the desired category')" @keyup="searchCategory($event)"
          ref="inputCategpry" class="input w-full bg-blue-custom-1 text-gray-custom-1 placeholder-gray-custom-1" />
        <div class="flex items-center text-sm mt-2" v-if="categoryParents.length">
          <button :title="__('Back')" class="btn btn-sm btn-outline border-none hover:bg-gray-300 btn-circle"
            @click="selectCategory($event, categoryParents[categoryParents.length - 1])">
            <ArrowSmallRightIcon class="w-5 h-5 text-black" />
          </button>
          <p class="mr-1">{{ categoryParents[categoryParents.length - 1].title }}</p>
        </div>
        <p v-else class="text-sm mt-3">{{ __('You can choose one or more categories at the same time') }}</p>
        <div class="card mt-3 bg-base-200 h-56 category md:h-auto">
          <div class="card-body px-2 py-4 md:p-4">
            <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1 md:gap-5 items-baseline">
              <div class="hover:bg-gray-100/40 hover:rounded-lg relative" v-for="category in filterCategories"
                @click="selectCategory($event, category)" :class="{ 'cursor-pointer': category.children.length }" v-show="!category?.hidden">
                <input type="checkbox" :value="category.id" v-model="selectedCategory"
                  class="checkbox checkbox-sm absolute top-0 right-0 border-gray-400" />
                <button
                  class="btn btn-circle animate-none btn-xs !bg-primary border-none w-5 h-5 min-h-0 absolute top-0 left-6"
                  v-if="category.children.length">{{ category.children.length + '+' }}</button>
                <img :src="category.image" class="w-16 h-16 m-auto" alt="image" />
                <p class="text-center mt-1 text-sm text-black">{{ category.title }}</p>
              </div>
            </div>
            <NotFound v-if="!filterCategories.filter(item => !item.hidden).length" />
          </div>
        </div>
        <div class="modal-action items-center">
          <p class="text-sm w-full" v-if="selectedCategory.length">
            {{ selectedCategory.length + ' ' + __('The category is selected') }}</p>
          <label class="btn btn-success btn-sm" for="category-modal"
            @click="setQuery('category', selectedCategory.join(','))">{{ __('anti filter')
            }}</label>
          <label class="btn btn-error btn-sm" for="category-modal">{{ __('Close') }}</label>
        </div>
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="brand-modal" class="modal-toggle" />
    <label class="modal" for="brand-modal">
      <label class="modal-box max-w-5xl" for="">
        <input type="text" :placeholder="__('Search for the desired brand')" @keyup="searchCategory($event, 'brand')"
          class="input w-full bg-blue-custom-1 text-gray-custom-1 placeholder-gray-custom-1" />
        <p class="text-sm mt-3">{{ __('You can choose one or more brands at the same time') }}</p>
        <div class="card mt-3 bg-base-200 h-56 category md:h-auto">
          <div class="card-body px-2 py-4 md:p-4">
            <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1 md:gap-5 items-baseline">
              <label class="cursor-pointer hover:bg-gray-100/40 hover:rounded-lg relative" v-for="category in brands"
                v-show="!category?.hidden">
                <input type="checkbox" :value="category.id" v-model="selectedBrand"
                  class="checkbox checkbox-sm absolute top-0 right-0 border-gray-400" />
                <img :src="category.image" class="w-16 h-16 m-auto" alt="image" />
                <p class="text-center mt-1 text-sm text-black">{{ category.title }}</p>
              </label>
            </div>
            <NotFound v-if="!brands.filter(item => !item.hidden).length" />
          </div>
        </div>
        <div class="modal-action items-center">
          <p class="text-sm w-full" v-if="selectedBrand.length">
            {{ selectedBrand.length + ' ' + __('The brand is selected') }}</p>
          <label class="btn btn-success btn-sm" for="brand-modal" @click="setQuery('brand', selectedBrand.join(','))">{{
            __('anti filter')
          }}</label>
          <label class="btn btn-error btn-sm" for="brand-modal">{{ __('Close') }}</label>
        </div>
      </label>
    </label>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="car-modal" class="modal-toggle" />
    <label class="modal" for="car-modal">
      <label class="modal-box max-w-5xl" for="">
        <input type="text" :placeholder="__('Search for the desired car. Example: Pride')"
          @keyup="searchCategory($event, 'car')"
          class="input w-full bg-blue-custom-1 text-gray-custom-1 placeholder-gray-custom-1" />
        <p class="text-sm mt-3">{{ __('You can choose one or more cars at the same time') }}</p>
        <div class="card mt-3 bg-base-200 h-56 category md:h-auto">
          <div class="card-body px-2 py-4 md:p-4">
            <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1 md:gap-5 items-baseline">
              <label class="cursor-pointer hover:bg-gray-100/40 hover:rounded-lg relative" v-for="category in cars"
                v-show="!category?.hidden">
                <input type="checkbox" :value="category.id" v-model="selectedCar"
                  class="checkbox checkbox-sm absolute top-0 right-0 border-gray-400" />
                <img :src="category.image" class="w-16 h-16 m-auto" alt="image" />
                <p class="text-center mt-1 text-sm text-black">{{ category.title }}</p>
              </label>
            </div>
            <NotFound v-if="!cars.filter(item => !item.hidden).length" />
          </div>
        </div>
        <div class="modal-action items-center">
          <p class="text-sm w-full" v-if="selectedCar.length">
            {{ selectedCar.length + ' ' + __('The car is selected') }}
          </p>
          <label class="btn btn-success btn-sm" for="car-modal" @click="setQuery('car', selectedCar.join(','))">
          {{ __('anti filter')}}
        </label>
          <label class="btn btn-error btn-sm" for="car-modal">{{ __('Close') }}</label>
        </div>
      </label>
    </label>
  </Teleport>
</template>
<script setup>
import useProducts from "../../../composables/shop/product";
import { watch, onMounted, onUpdated, onBeforeUnmount, ref } from 'vue';
import {
  AdjustmentsHorizontalIcon,
  Squares2X2Icon,
  ListBulletIcon,
  Bars3BottomLeftIcon,
  ArrowPathIcon,
  ArrowSmallRightIcon,
  FunnelIcon
} from '@heroicons/vue/24/outline'
import ProductItem from "./ProductItem.vue";
import LeftSidebar from "../layout/LeftSidebar.vue";
import ProductModal from "../modal/ProductModal.vue";
import ReserveModal from "../modal/ReserveModal.vue";
import ProductGridItem from "./ProductGridItem.vue";
import LoadMore from "../../layout/LoadMore.vue";
import ProductQuantityModal from "../modal/ProductQuantityModal.vue";
import AnimatedNotice from "../layout/AnimatedNotice.vue";
let timeout = null;
let update = null;
let scrollbar = null;
const categoryParents = ref([]);
const inputCategpry = ref(null);
const nikran = window.nikran;
const noticeMessages = [];
const { brands, cars, products, route, router, product, errors, loading, meta, filterCategories, selectedCar, selectedCategory, categories, selectedBrand, controller, exportData, reserve, changeQuantity, exportProduct, getCategories, getProducts, getProduct, grid, changeLayout } = useProducts();
const search = ref(route.query.q || '');
if(nikran.special_alert && route.name == 'product.special') noticeMessages.push('<span class="animate-pulse">'+nikran.special_alert+'</span>');
else noticeMessages.push('<span class="inline-flex !text-blue-custom-4 gap-2"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="stroke-current flex-shrink-0 h-6 w-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>' 
+ window.i18n['Latest information update'] + ' ' + nikran.product.ago + '</span>');
watch(search, () => searchProduct());
const searchProduct = () => {
  if (timeout) clearTimeout(timeout)
  if (controller.value) controller.value.abort();
  const value = search.value;
  const oldValue = route.query.q || '';
  if (value !== oldValue) timeout = setTimeout(() => {
    setQuery('q', value);
    if (scrollbar) scrollbar.scrollTop = 0;
  }, 500);
}
const selectCategory = (event, category) => {
  if (event.target.type !== 'checkbox' && category.children.length) {
    const i = categoryParents.value.findIndex(item => item.id == category.id);
    if (i > -1) {
      categoryParents.value.splice(i, 1);
      if (category.parent_id) filterCategories.value = categories.value.find(item => item.id == category.parent_id).children;
      else filterCategories.value = categories.value;
    } else {
      categoryParents.value.push(category);
      filterCategories.value = category.children;
    }
    inputCategpry.value.value = null;
    filterCategories.value.map(item => {
      item['hidden'] = false;
    });
    return false;
  }
  const id = category.id;
  const index = filterCategories.value.findIndex(item => item.id === id);
  filterCategories.value[index]['checked'] = !filterCategories.value[index]['checked'];
}
const setQuery = (key, value) => {
  getProducts(window.setQuery(router, route, key, value));
}
const searchCategory = (e, attribute = '') => {
  let data = attribute ? (attribute === 'brand' ? brands.value : cars.value) : filterCategories.value;
  data = data.map(item => {
    item['hidden'] = !item.title.includes(e.target.value);
    return item;
  });
}
const showProduct = (slug) => getProduct(slug);
const getData = () => {
  getProducts(route.query);
  document.activeElement.blur();
}
onBeforeUnmount(() => clearTimeout(update));
onMounted(() => {
  getData();
  window.onhashchange = () => {
    const productModal = document.getElementById('product-modal');
    if (productModal.checked && !window.location.hash) productModal.checked = false;
  }
});
onUpdated(() => {
  scrollbar = document.querySelector('.product-item');
  update = setTimeout(() => {
    const elm = document.getElementById('alert-update');
    if (elm) elm.style.display = 'none'
  }, 5000);
  if (scrollbar) scrollbar.addEventListener('scroll', (e) => {
    if (meta.current_page < meta.last_page && !loading.value && !errors.value && (scrollbar.scrollTop + scrollbar.offsetHeight) >= (scrollbar.scrollHeight - 8)) {
      getProducts({ ...route.query, page: meta.current_page + 1 });
    }
  });
});
</script>
